import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : react components for config system
// =============================================================================
// ---Third-party imports
import React, { useEffect, useState, Fragment } from 'react';
import { Table, Checkbox, Input, Dropdown, Menu, Icon, Segment, Button, TextArea } from 'semantic-ui-react';
// ---Framework imports
import log from 'uix/logging';
import { get, strip, keys, dict, groupBy, size, isArray, isObject, isEqual, mapItems, intersection, isNil } from 'ldash';
import { Sel, Actions } from 'uix/registries';
import connect from 'uix/connect';
import { selectRequestSubdomains } from 'uix/selectors';
import { AutoPortal, HeaderSettingsIcon, XMenu, ResizableDialog, ApiErrorMessage, Link, Dialog } from 'uix/components';
import { useSelector, usePersistentShallow, usePersistent, useActions, usePartContext, useMemo, useApi, useCallApi, useResettableState } from 'uix/hooks';
import { DomainTable } from 'uix/domain/dtable';
// ---Local imports
import { saveDomainConfigValues, rebuildDomainConfig } from './actions';
import styles from './styles.scss';
import { useEditedDomainConfigValues, useDomainConfigEditDetails } from './hooks';
// ---Globals
const DELETE = '__DELETE__';
const NO_VALUE = '__NO_VALUE__';
const stringRepr = (value)=>{
    if (value === null || value === undefined) {
        return '';
    } else if (isObject(value)) {
        // strdict
        return mapItems(value, (k, v)=>`${k}: ${v}`).join(', ');
    }
    return String(value);
};
const ApiKeyFileContentEditor = ({ name, editedValue, overlayValue })=>{
    let is_modified = editedValue && editedValue != overlayValue;
    let is_file = overlayValue && (overlayValue.startsWith('/') || overlayValue.startsWith('./'));
    let enabled = !is_modified && is_file;
    let { data, reload } = useApi('config/readApiKeyFile', {
        name
    }, {
        condition: enabled,
        deps: [
            overlayValue
        ]
    });
    let { callApi: saveApiKeyFile } = useCallApi('config/writeApiKeyFile');
    let [modified_data, set_modified_data] = useResettableState(null, [
        overlayValue
    ]);
    let successNotification = useActions('successNotification');
    let onSaved = (data)=>{
        // error notification is handled in callApi
        if (data) successNotification({
            message: `ApiKey ${data.state}`,
            dismissAfter: 2000
        });
        reload();
    };
    let render_data = modified_data === null ? data?.keydata : modified_data;
    if (!enabled) return null;
    return /*#__PURE__*/ _jsx(Dialog, {
        size: "small",
        icon: "edit",
        header: "Key",
        subheader: data?.filename || overlayValue,
        onCancel: ()=>set_modified_data(null),
        onSubmit: ()=>saveApiKeyFile({
                name,
                keydata: modified_data
            }, onSaved),
        submitDisabled: modified_data === null,
        trigger: /*#__PURE__*/ _jsx(Button, {
            icon: "edit",
            color: render_data ? undefined : 'red',
            compact: true,
            basic: true,
            size: "mini"
        }),
        children: /*#__PURE__*/ _jsx(TextArea, {
            onChange: (e, data)=>set_modified_data(data.value),
            style: {
                width: '100%',
                height: '100px',
                resize: 'vertical'
            },
            value: render_data || ''
        })
    });
};
const renderCustomEditor = ({ name, spec, ...valueProps })=>{
    //~ log.x(name)
    if (spec.startsWith('apikey')) {
        return /*#__PURE__*/ _jsx(ApiKeyFileContentEditor, {
            name: name,
            ...valueProps
        });
    }
    return null;
};
const getEditor = (dname, name, spec, value, defaultValue, overlayValue, editedValue, setEditedValue)=>{
    let type = spec.split('(')[0];
    // like "default=None" or "zip, tar, default=zip"
    let rest = spec.split('(').slice(1).join('(').slice(0, -1);
    let parts = rest.split(',').map((e)=>e.trim());
    let args = parts.filter((e)=>!e.includes('='));
    let kwargs = dict(parts.filter((e)=>e.includes('=')).map((e)=>e.split('=')));
    let plainDefaultValue = get(kwargs, 'default', '');
    let setValue = (newValue, prevValue)=>{
        //~ log('set %o to %o [prev: %o]', name, newValue, prevValue)
        if (prevValue !== undefined && prevValue === newValue) {
            setEditedValue(name, undefined);
        } else {
            setEditedValue(name, newValue);
        }
    };
    let currentValue = editedValue !== undefined ? editedValue : overlayValue;
    //~ return spec
    let res = spec;
    if (type == 'boolean') {
        //checkbox needs always a value
        let checked = currentValue !== undefined ? currentValue : value;
        res = /*#__PURE__*/ _jsx(Checkbox, {
            toggle: true,
            checked: checked,
            onChange: (event, data)=>setValue(data.checked === true, overlayValue)
        });
    }
    let placeholderValue = stringRepr(value) || stringRepr(defaultValue);
    //~ log(name, type, value)
    if ([
        'string',
        'integer',
        'float',
        'password',
        'rgbcolor',
        'xml',
        'strdict',
        'apikey'
    ].includes(type)) {
        let set_v = (v)=>setValue(v, stringRepr(overlayValue));
        res = /*#__PURE__*/ _jsxs("div", {
            style: {
                display: 'flex'
            },
            children: [
                /*#__PURE__*/ _jsx(Input, {
                    placeholder: placeholderValue,
                    value: stringRepr(currentValue),
                    onChange: (e, data)=>set_v(data.value)
                }),
                renderCustomEditor({
                    name,
                    spec,
                    editedValue,
                    currentValue,
                    overlayValue,
                    placeholderValue,
                    setValue: set_v
                })
            ]
        });
    }
    if (type == 'set' || type.endsWith('_list')) {
        //~ log(kwargs)
        res = /*#__PURE__*/ _jsx(Input, {
            placeholder: placeholderValue,
            value: stringRepr(currentValue),
            onChange: (e, data)=>setValue(data.value, stringRepr(overlayValue))
        });
    }
    if (type == 'option') {
        let opts = args.map((v)=>{
            v = strip(v, "'");
            return {
                key: v,
                value: v,
                text: v,
                onClick: ()=>setValue(v, overlayValue)
            };
        });
        if (!overlayValue) {
            opts.push({
                key: NO_VALUE,
                value: NO_VALUE,
                text: 'Kein Wert',
                onClick: ()=>setValue('None', overlayValue)
            });
        }
        if (overlayValue) {
            opts.push({
                key: DELETE,
                value: DELETE,
                text: 'Entfernen',
                onClick: ()=>setValue(DELETE, overlayValue)
            });
        }
        res = /*#__PURE__*/ _jsx(Dropdown, {
            placeholder: placeholderValue,
            fluid: true,
            selection: true,
            options: opts,
            value: currentValue !== null ? stringRepr(currentValue) : NO_VALUE
        });
    }
    return res;
};
let numdots = (s)=>(s.match(/\./g) || []).length;
let fullname2sec = (s)=>s.split('.').slice(0, -1).join('.');
export let SysReload = connect('SysReload', {
    storeMap: (root)=>({
            dirty: root.config.dconfigDirty,
            subdomains: selectRequestSubdomains(root),
            dname: root.meta.request.dname,
            rebuildConfigActive: root.debug.rebuildConfigActive
        }),
    actionMap: {
        rebuildDomainConfig
    }
}, ({ dirty, subdomains, rebuildDomainConfig, dname, rebuildConfigActive })=>{
    let dirtySubdomains = intersection(dirty, subdomains.map((d)=>d.name));
    return /*#__PURE__*/ _jsx(Menu.Item, {
        title: `Reload needed: ${size(dirtySubdomains)} domains`,
        style: {
            fontWeight: 'bold',
            color: 'red'
        },
        onClick: ()=>rebuildDomainConfig(dname),
        icon: {
            name: 'refresh',
            loading: rebuildConfigActive
        },
        disabled: size(dirtySubdomains) == 0
    });
});
/* simple form of SysReload */ export const DomainReloadMenuItem = ({})=>{
    let request = useSelector(Sel.request);
    let rebuildDomainConfig = useActions('rebuildDomainConfig');
    let rebuildConfigActive = useSelector((root)=>root.debug.rebuildConfigActive);
    return /*#__PURE__*/ _jsx(Menu.Item, {
        onClick: ()=>rebuildDomainConfig(request.dname),
        icon: {
            name: 'refresh',
            loading: rebuildConfigActive
        },
        content: "Reload Config",
        disabled: rebuildConfigActive
    });
};
let useConfigOptions = ()=>usePersistentShallow('ConfigMenuOptions', {
        showComments: false,
        showDefaults: false,
        showModifiedDefault: false,
        showModifiedOverlay: false,
        showAll: false,
        editMode: false,
        search: ''
    }, 3);
export const ConfigMenu = ({ showAll = false, basic = false, rebuildDomainConfig, onSave, portalTarget })=>{
    let { i18n, dname } = usePartContext();
    let [editedValues, editValue] = useEditedDomainConfigValues(dname);
    let [opts, setOpts] = useConfigOptions();
    let saveDomainConfigValues = useActions(Actions.saveDomainConfigValues);
    let visible = {} // all
    ;
    //~ if (options === 'simple') {
    //~ visible.editMode = true
    //~ visible.showComments = true
    //~ }
    if (basic) {
        visible = {
            showComments: true,
            editMode: true
        };
    }
    let isVisible = (name)=>size(visible) == 0 || visible[name];
    let Toggle = ({ name, icon, color = 'black' })=>{
        if (!isVisible(name)) {
            return null;
        }
        let toggle = ()=>{
            if (name == 'showModifiedOverlay') {
                setOpts({
                    showModifiedDefault: false
                });
            } else if (name == 'showModifiedDefault') {
                setOpts({
                    showModifiedOverlay: false
                });
            }
            setOpts({
                [name]: !opts[name]
            });
        };
        return /*#__PURE__*/ _jsx(Menu.Item, {
            active: opts[name],
            onClick: toggle,
            icon: {
                name: icon,
                color: color
            },
            title: i18n.get(`btn_${name}`)
        });
    };
    let saveModified = ()=>{
        // remove "root." prefix
        let cleaned = dict(mapItems(editedValues, (name, value)=>[
                name.slice(5),
                value
            ]));
        saveDomainConfigValues(dname, cleaned);
        if (onSave) {
            onSave();
        }
    };
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        children: [
            /*#__PURE__*/ _jsx(Toggle, {
                name: "editMode",
                icon: "edit outline"
            }),
            /*#__PURE__*/ _jsx(Toggle, {
                name: "showDefaults",
                icon: "sticky note outline"
            }),
            /*#__PURE__*/ _jsx(Toggle, {
                name: "showComments",
                icon: "info"
            }),
            /*#__PURE__*/ _jsx(Toggle, {
                name: "showModifiedDefault",
                icon: "filter",
                color: "teal"
            }),
            /*#__PURE__*/ _jsx(Toggle, {
                name: "showModifiedOverlay",
                icon: "filter",
                color: "blue"
            }),
            /*#__PURE__*/ _jsx(Toggle, {
                name: "showAll",
                icon: "eye"
            }),
            isVisible('filter') ? /*#__PURE__*/ _jsxs(XMenu.Item, {
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        name: "filter"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        size: "small",
                        value: opts.search,
                        onChange: (event, data)=>setOpts({
                                search: data.value
                            })
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "undo",
                disabled: size(editedValues) == 0,
                title: "Undo Changes",
                onClick: ()=>editValue(null)
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "save",
                disabled: size(editedValues) == 0,
                title: `Save ${size(editedValues)} modified values`,
                onClick: saveModified
            }),
            /*#__PURE__*/ _jsx(SysReload, {})
        ]
    });
    if (portalTarget) {
        menu = /*#__PURE__*/ _jsx(AutoPortal, {
            target: portalTarget,
            children: menu
        });
    }
    return menu;
};
const renderConfigScalar = (value)=>{
    if (value === undefined) {
        value = '...'; // loading
    } else if (value === null) {
        value = '';
    } else if (isArray(value)) {
        value = value.map((v)=>String(v)).join(', ');
    } else if (isObject(value)) {
        // strdict
        value = mapItems(value, (k, v)=>`${k}:${v}`).join(', ');
    }
    return /*#__PURE__*/ _jsx("tt", {
        children: `${value}`
    });
};
export const DomainConfigTable = ({ names, section, include, exclude, menu = false, menuPortal = 'ConfigMenuPortal', basic, flat = false, onSave, levels = 3 })=>{
    let { dname, i18n } = usePartContext();
    levels = parseInt(levels);
    let [opts] = useConfigOptions();
    if (section && opts.showAll) {
        names = null;
    }
    let [inspectValue, setInspectValue] = useState(null);
    let [editedValues, editValue] = useEditedDomainConfigValues(dname);
    let { spec: data } = useDomainConfigEditDetails({
        section,
        names,
        levels,
        exclude,
        include
    });
    if (!data) {
        return null;
    }
    data = data.filter((d)=>d.level > 0 && d.name);
    //~ search = 'lic'
    if (opts.search) {
        let search_t = opts.search.trim();
        if (search_t.includes(' ')) {
            let terms = search_t.split(' ').map((e)=>e.toLowerCase());
            data = data.filter((d)=>terms.every((t)=>d.name.toLowerCase().includes(t)));
        } else {
            data = data.filter((d)=>d.name.toLowerCase().includes(search_t.toLowerCase()));
        }
    }
    if (opts.showModifiedDefault) {
        data = data.filter((d)=>String(d.defaultValue) !== String(d.value));
    } else if (opts.showModifiedOverlay) {
        data = data.filter((d)=>d.overlayValue !== undefined);
    }
    let remainingNames = data.map((d)=>d.name);
    let subsects = groupBy(remainingNames, (e)=>fullname2sec(e));
    data = data.map((d)=>({
            ...d,
            firstOfSec: subsects[fullname2sec(d.name)][0] === d.name
        }));
    let sectionOffset = section ? numdots(section) : 0;
    let renderValue = (name, value, spec, defaultValue, overlayValue)=>{
        if (opts.editMode && spec) {
            return getEditor(dname, name, spec, value, defaultValue, overlayValue, editedValues[name], editValue);
        }
        return renderConfigScalar(value);
    };
    let levelPad = flat ? 0 : 10 // increase padding for each level
    ;
    let paddingLeft = flat ? 5 : 0;
    let rows = data.map(({ name, label, value, defaultValue, overlayValue, comments, spec, level, firstOfSec })=>/*#__PURE__*/ _jsxs(Fragment, {
            children: [
                fullname2sec(name) !== section && firstOfSec ? /*#__PURE__*/ _jsx(Table.Row, {
                    children: /*#__PURE__*/ _jsx(Table.Cell, {
                        colSpan: "99",
                        style: {
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            fontWeight: 'normal',
                            paddingLeft: paddingLeft + levelPad * (level - 1) - levelPad / 2
                        },
                        children: /*#__PURE__*/ _jsxs("tt", {
                            children: [
                                "[",
                                name.split('.').slice(sectionOffset + 1, -1).filter((e)=>e !== '__many__') // remove from rendered name
                                .join('.'),
                                "]"
                            ]
                        })
                    })
                }) : null,
                /*#__PURE__*/ _jsxs(Table.Row, {
                    children: [
                        /*#__PURE__*/ _jsx(Table.Cell, {
                            onClick: ()=>setInspectValue(name),
                            style: {
                                cursor: 'pointer',
                                paddingLeft: paddingLeft + levelPad * (level - 1) + levelPad / 2
                            },
                            children: /*#__PURE__*/ _jsx("tt", {
                                children: label
                            })
                        }),
                        /*#__PURE__*/ _jsx(Table.Cell, {
                            children: renderValue(name, value, spec, defaultValue, overlayValue)
                        }),
                        opts.showDefaults ? /*#__PURE__*/ _jsx(_Fragment, {
                            children: /*#__PURE__*/ _jsx(Table.Cell, {
                                style: {
                                    color: '#AAA',
                                    cursor: 'pointer'
                                },
                                title: `${spec}`,
                                onClick: ()=>setInspectValue(name),
                                children: /*#__PURE__*/ _jsx("tt", {
                                    children: renderConfigScalar(defaultValue)
                                })
                            })
                        }) : null,
                        name == inspectValue && /*#__PURE__*/ _jsx(ResizableDialog, {
                            header: /*#__PURE__*/ _jsx("tt", {
                                children: name
                            }),
                            name: "DomainTreeConfigChain",
                            closeOnEsc: true,
                            onClose: ()=>setInspectValue(null),
                            children: /*#__PURE__*/ _jsx(DomainTreeConfigChain, {
                                names: [
                                    name.slice(5)
                                ]
                            })
                        })
                    ]
                }),
                opts.showComments && comments && comments.length > 0 ? /*#__PURE__*/ _jsx(Table.Row, {
                    children: /*#__PURE__*/ _jsx(Table.Cell, {
                        colSpan: "99",
                        style: {
                            backgroundColor: 'rgba(0,0,0,0)',
                            fontWeight: 'normal'
                        },
                        children: /*#__PURE__*/ _jsx("i", {
                            children: comments.map((c, i)=>/*#__PURE__*/ _jsxs(Fragment, {
                                    children: [
                                        c,
                                        /*#__PURE__*/ _jsx("br", {})
                                    ]
                                }, i))
                        })
                    })
                }) : null
            ]
        }, name + label));
    let theMenu = /*#__PURE__*/ _jsx(ConfigMenu, {
        showAll: false,
        onSave: onSave,
        portalTarget: menuPortal,
        basic: menu == 'basic'
    });
    let table = /*#__PURE__*/ _jsxs(Table, {
        definition: true,
        className: "dconfigTree",
        children: [
            menu ? theMenu : null,
            /*#__PURE__*/ _jsx(Table.Header, {
                children: /*#__PURE__*/ _jsxs(Table.Row, {
                    children: [
                        /*#__PURE__*/ _jsx(Table.HeaderCell, {
                            children: section == 'root' ? dname : section
                        }),
                        /*#__PURE__*/ _jsx(Table.HeaderCell, {
                            children: i18n.get('config_value')
                        }),
                        opts.showDefaults ? /*#__PURE__*/ _jsx(Table.HeaderCell, {
                            children: "Default"
                        }) : null
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Table.Body, {
                children: rows
            })
        ]
    });
    return basic ? table : /*#__PURE__*/ _jsx(Segment, {
        children: table
    });
};
export const DomainConfigSegment = ({ subheader = '', ...props })=>{
    let elements = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(HeaderSettingsIcon, {
                icon: "configure",
                header: "Konfiguration",
                subheader: subheader,
                portal: "ConfigMenuPortal"
            }),
            /*#__PURE__*/ _jsx(DomainConfigTable, {
                menu: true,
                ...props
            })
        ]
    });
    return /*#__PURE__*/ _jsx(Segment, {
        children: elements
    });
};
export const DomainTreeConfigChain = ({ names })=>{
    //~ let names = ['connectors.sruImport']
    let [mergeSource, setMergeSource] = usePersistent('mergeSource', false);
    let [showDname, setShowDname] = usePersistent('showDname', false);
    let { data, error } = useApi('analyzeConfigValues', {
        names,
        mergeInfo: mergeSource
    });
    let cols = [];
    let table_data = useMemo(()=>data?.analyzedConfig || {}, [
        data
    ]);
    cols = names.map((n)=>({
            id: n,
            Header: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    mergeSource && /*#__PURE__*/ _jsx("span", {
                        style: {
                            float: 'right'
                        },
                        children: "Source"
                    }),
                    n
                ]
            }),
            accessor: (r)=>r[n]?.value,
            Cell: ({ value, original })=>{
                let source = original[n]?.source || '';
                let parent = table_data[original.parent]?.[n];
                let parent_source = parent?.source || '';
                let parent_value = parent?.value;
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        mergeSource && source !== parent_source && /*#__PURE__*/ _jsx(Link, {
                            lazy: true,
                            dname: original.name,
                            classname: "config",
                            tabname: "chain",
                            query: {
                                select: source
                            },
                            style: {
                                float: 'right'
                            },
                            children: source
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            style: {
                                fontWeight: String(value) === String(parent_value) ? undefined : 'bold'
                            },
                            children: renderConfigScalar(value)
                        })
                    ]
                });
            }
        }));
    if (error) {
        return /*#__PURE__*/ _jsx(ApiErrorMessage, {
            error: error,
            traceback: true
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(XMenu, {
                attached: true,
                size: "tiny",
                children: [
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        content: "MergeSource",
                        set: setMergeSource,
                        value: mergeSource
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        content: "Domain-Namen",
                        set: setShowDname,
                        value: showDname
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    flex: 1,
                    display: 'flex',
                    overflow: 'hidden'
                },
                children: /*#__PURE__*/ _jsx(DomainTable, {
                    data: table_data,
                    columns: cols,
                    compact: true,
                    flex: true,
                    showAncestors: true,
                    showDomainName: showDname
                })
            })
        ]
    });
};
